import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Select from 'react-select';

const HaqdarshakLeaderboard = () => {
  const [topHds, setTopHds] = useState([]);
  const [bottomHds, setBottomHds] = useState([]);
  const [loading, setLoading] = useState({ top: false, bottom: false });
  const [error, setError] = useState({ top: null, bottom: null });
  const pid = useSelector((state) => state.user.projectId);

  const [orgOptions, setOrgOptions] = useState([]);
  const [schemeTypeOptions, setSchemeTypeOptions] = useState([]);
  
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [selectedSchemeTypes, setSelectedSchemeTypes] = useState([]);
  const [filterTimeout, setFilterTimeout] = useState(null);

  // Debounce filter changes to prevent too many API calls
  const handleFilterChange = () => {
    if (filterTimeout) {
      clearTimeout(filterTimeout);
    }
    
    setFilterTimeout(setTimeout(() => {
      fetchTopHds();
      fetchBottomHds();
    }, 500));
  };

  const getQueryParams = () => {
    const params = new URLSearchParams();
    params.append('pid', pid);
    selectedOrgs.forEach(org => params.append('OrgName', org.value));
    selectedSchemeTypes.forEach(type => params.append('SchemeType', type.value));
    return params;
  };

  const fetchOrgOptions = async () => {
    try {
      const response = await fetch(`https://api-project360-insights.haqdarshak.com/kpi/waterfall/unique-org/?pid=${pid}`);
      const data = await response.json();
      setOrgOptions(data.unique_Organizations.map(org => ({ value: org, label: org })));
    } catch (error) {
      console.error('Error fetching org options:', error);
    }
  };

  const fetchSchemeTypeOptions = async () => {
    try {
      const response = await fetch(`https://api-project360-insights.haqdarshak.com/kpi/waterfall/unique-scheme-type/?pid=${pid}`);
      const data = await response.json();
      
      const schemeTypeMap = {
        'sch': 'Schemes',
        'doc': 'Document',
        'dfl': 'DFL',
        'yc': 'Yojana Card'
      };
      
      setSchemeTypeOptions(data.unique_Scheme_Types.map(type => ({
        value: type,
        label: schemeTypeMap[type] || type
      })));
    } catch (error) {
      console.error('Error fetching scheme type options:', error);
    }
  };

  const fetchTopHds = async () => {
    setLoading(prev => ({ ...prev, top: true }));
    setError(prev => ({ ...prev, top: null }));
    try {
      const response = await fetch(
        `https://api-project360-insights.haqdarshak.com/kpi/comparison/top-hds/?${getQueryParams()}`
      );
      if (!response.ok) throw new Error('Failed to fetch top HDs');
      const data = await response.json();
      setTopHds(data.top_hds || []);
    } catch (err) {
      setError(prev => ({ ...prev, top: err.message }));
      console.error('Error fetching top HDs:', err);
    } finally {
      setLoading(prev => ({ ...prev, top: false }));
    }
  };

  const fetchBottomHds = async () => {
    setLoading(prev => ({ ...prev, bottom: true }));
    setError(prev => ({ ...prev, bottom: null }));
    try {
      const response = await fetch(
        `https://api-project360-insights.haqdarshak.com/kpi/comparison/bottom-hds/?${getQueryParams()}`
      );
      if (!response.ok) throw new Error('Failed to fetch bottom HDs');
      const data = await response.json();
      setBottomHds(data.bottom_hds || []);
    } catch (err) {
      setError(prev => ({ ...prev, bottom: err.message }));
      console.error('Error fetching bottom HDs:', err);
    } finally {
      setLoading(prev => ({ ...prev, bottom: false }));
    }
  };

  useEffect(() => {
    fetchOrgOptions();
    fetchSchemeTypeOptions();
    fetchTopHds();
    fetchBottomHds();
  }, [pid]);

  useEffect(() => {
    handleFilterChange();
    return () => {
      if (filterTimeout) {
        clearTimeout(filterTimeout);
      }
    };
  }, [selectedOrgs, selectedSchemeTypes]);

  const clearFilters = () => {
    setSelectedOrgs([]);
    setSelectedSchemeTypes([]);
  };

  const rankColors = [
    'bg-amber-100 text-amber-800',
    'bg-gray-100 text-gray-800',
    'bg-amber-50 text-amber-700',
    'bg-blue-50 text-blue-700',
  ];

  const getRankColor = (index) => {
    if (index === 0) return rankColors[0];
    if (index === 1) return rankColors[1];
    if (index === 2) return rankColors[2];
    return rankColors[3];
  };

  return (
    <div className="p-6 ml-[5vw] w-full md:w-[90vw] bg-gray-50 min-h-screen">
      <h1 className="text-2xl font-bold text-gray-800 ml-[10vw] text-center">HD Performance Leaderboard Based On BR Count</h1>

      <div className="ml-12">
        <div className="bg-white rounded-lg shadow p-6 mb-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
            <Select
              isMulti
              options={orgOptions}
              value={selectedOrgs}
              onChange={(selected) => setSelectedOrgs(selected || [])}
              className="basic-multi-select"
              classNamePrefix="select"
              placeholder="Select organizations..."
              closeMenuOnSelect={false}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Scheme Type</label>
            <Select
              isMulti
              options={schemeTypeOptions}
              value={selectedSchemeTypes}
              onChange={(selected) => setSelectedSchemeTypes(selected || [])}
              className="basic-multi-select"
              classNamePrefix="select"
              placeholder="Select service types..."
              closeMenuOnSelect={false}
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>
      
      <div className="ml-[7vw] grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top 10 HDs Card */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-4 py-3" style={{backgroundColor:"#CF87C4"}}>
            <h2 className="text-lg font-semibold text-white">Top 10 Performers</h2>
          </div>
          
          {loading.top ? (
            <div className="p-8 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-600"></div>
            </div>
          ) : error.top ? (
            <div className="p-4 text-red-500">{error.top}</div>
          ) : (
            <div className="divide-y divide-gray-200">
              {topHds.slice(0, 10).map((hd, index) => (
                <div key={hd.HDID} className="px-4 py-1 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center space-x-4">
                    <span className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full ${getRankColor(index)} font-medium`}>
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {hd.haqdarshakName || hd.hd_name}
                      </p>
                      <p className="text-sm text-gray-500">HDID: {hd.HDID}</p>
                    </div>
                    <div className="inline-flex items-center text-base font-semibold text-gray-900">
                      {hd.br_count.toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bottom 10 HDs Card */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-4 py-3" style={{backgroundColor:"#E1DEDB"}}>
            <h2 className="text-lg font-semibold text-white">Bottom 10 Performers</h2>
          </div>
          
          {loading.bottom ? (
            <div className="p-8 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-red-600"></div>
            </div>
          ) : error.bottom ? (
            <div className="p-4 text-red-500">{error.bottom}</div>
          ) : (
            <div className="divide-y divide-gray-200">
              {bottomHds.slice(0, 10).map((hd, index) => (
                <div key={hd.HDID} className="px-4 py-1 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center space-x-4">
                    <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-800 font-medium">
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {hd.haqdarshakName || hd.hd_name}
                      </p>
                      <p className="text-sm text-gray-500">HDID: {hd.HDID}</p>
                    </div>
                    <div className="inline-flex items-center text-base font-semibold text-gray-900">
                      {hd.br_count.toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HaqdarshakLeaderboard;